import React, { useState } from "react";
import { ClipboardList, ChevronDown, ChevronUp, Image as ImageIcon } from "lucide-react";
import DetalleMontaje from "@/components/DetalleMontaje";
import { cloudinaryThumb } from "@/lib/cloudinary";

// Resumen compacto para la lista izquierda de Reportes (misma fuente: reporte.detalle_montaje).
export function ResumenMontajeLista({ r }) {
  const det = r.detalle_montaje;
  const piezas = det?.piezas || [];
  const key = `${r.ruta_id}-${r.idx}`;
  const comun = det?.comun || {};
  const p0 = piezas[0] || {};
  const material = comun.material || p0.material;
  const medidas = comun.medidas || p0.medidas;
  const cantidad = piezas.reduce((a, x) => a + (Number(x.cantidad) || 1), 0);
  const nDistintas = piezas.length;
  const descs = piezas.map((x) => x.descripcion || x.nombre).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).slice(0, 2);
  const instr = det?.descripcion_linea || det?.notas_linea || r.parada?.observaciones;
  if (!det && !r.peticion?.codigo) return null;
  return (
    <div className="mt-1 space-y-0.5" data-testid={`resumen-montaje-${key}`}>
      <div className="text-[10px] text-zinc-400 truncate">
        <span className="font-mono text-zinc-300">{r.peticion?.codigo}</span>
        {r.peticion?.nombre && <> · {r.peticion.nombre}</>}
        {(r.peticion?.marca || r.cadena_nombre) && <> · {r.peticion?.marca || r.cadena_nombre}</>}
      </div>
      {(det?.referencia_cliente || descs.length > 0) && (
        <div className="text-[11px] text-amber-200 font-medium truncate" data-testid={`resumen-desc-${key}`}>
          {det?.referencia_cliente && <span className="font-mono text-amber-300 mr-1" data-testid={`resumen-ref-${key}`}>{det.referencia_cliente}</span>}
          {descs.join(" · ")}
        </div>
      )}
      {(material || medidas || cantidad > 0 || det?.zona) && (
        <div className="flex items-center gap-1 flex-wrap text-[9px]" data-testid={`resumen-badges-${key}`}>
          {cantidad > 0 && <span className="px-1 rounded bg-zinc-800 text-zinc-300">{cantidad} {cantidad === 1 ? "pieza" : "piezas"}{nDistintas > 1 && nDistintas !== cantidad ? ` (${nDistintas} tipos)` : ""}</span>}
          {material && <span className="px-1 rounded bg-zinc-800 text-zinc-300 truncate max-w-[110px]" title={material}>{material}</span>}
          {medidas && <span className="px-1 rounded bg-zinc-800 text-zinc-300 font-mono">{medidas}</span>}
          {det?.zona && <span className="px-1 rounded bg-sky-500/15 text-sky-300">{det.zona}</span>}
        </div>
      )}
      {instr && <div className="text-[10px] text-zinc-500 truncate" title={instr} data-testid={`resumen-instr-${key}`}>{instr}</div>}
    </div>
  );
}

// Bloque completo, plegable, antes de la galería del reporte seleccionado.
export function DetalleMontajeReporte({ reporte }) {
  const [abierto, setAbierto] = useState(true);
  const det = reporte.detalle_montaje;
  const imgs = reporte.imagenes_referencia || [];
  const dir = [reporte.espacio?.direccion, reporte.espacio?.cp, reporte.espacio?.ciudad].filter(Boolean).join(", ");
  return (
    <div className="bg-zinc-950/60 border border-amber-500/20 rounded-lg" data-testid="reporte-detalle-montaje">
      <button type="button" onClick={() => setAbierto((v) => !v)} data-testid="reporte-detalle-montaje-toggle"
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-amber-300">
        <span className="flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> DETALLE DEL MONTAJE
          {det?.n_piezas > 0 && <span className="text-zinc-400 font-normal">· {det.n_piezas} {det.n_piezas === 1 ? "pieza" : "piezas"}</span>}
          {det?.referencia_cliente && <span className="font-mono text-amber-200" data-testid="reporte-detalle-ref">· Ref. {det.referencia_cliente}</span>}
        </span>
        {abierto ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {abierto && (
        <div className="px-3 pb-3 space-y-2">
          <div className="text-[11px] text-zinc-400" data-testid="reporte-detalle-cabecera">
            <span className="text-zinc-200 font-semibold">{reporte.espacio?.nombre}</span>{dir && <> · {dir}</>}
            <br />
            <span className="font-mono text-zinc-300">{reporte.peticion?.codigo}</span>
            {reporte.peticion?.nombre && <> · {reporte.peticion.nombre}</>}
            {(reporte.peticion?.marca || reporte.cadena_nombre) && <> · Marca: {reporte.peticion?.marca || reporte.cadena_nombre}</>}
            {reporte.cliente_nombre && <> · Cliente: <span className="text-zinc-200">{reporte.cliente_nombre}</span></>}
            {det?.zona && <> · Zona: <span className="text-sky-300">{det.zona}</span></>}
          </div>
          {det?.piezas?.length > 0 ? (
            <DetalleMontaje detalle={det} campana={reporte.peticion?.nombre} marca={reporte.peticion?.marca || reporte.cadena_nombre} max={20} testId="reporte-detalle-piezas" />
          ) : (
            <div className="text-[11px] text-zinc-500 italic" data-testid="reporte-detalle-vacio">Sin componentes registrados para este trabajo{reporte.parada?.es_manual ? " (parada manual)" : ""}.</div>
          )}
          {reporte.parada?.observaciones && (
            <div className="text-[11px] bg-amber-500/10 border-l-2 border-amber-500/50 px-2 py-1 text-amber-100" data-testid="reporte-detalle-observaciones">
              <b>Instrucciones parada:</b> {reporte.parada.observaciones}
            </div>
          )}
          {imgs.length > 0 && (
            <div data-testid="reporte-imagenes-referencia">
              <div className="text-[10px] text-zinc-500 mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Referencia (render / AAFF / petición)</div>
              <div className="flex gap-1.5 flex-wrap">
                {imgs.map((u) => (
                  <a key={u} href={u} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded overflow-hidden border border-zinc-800 bg-zinc-900">
                    <img src={cloudinaryThumb ? cloudinaryThumb(u, 128) : u} alt="referencia" className="w-full h-full object-cover" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
